/**
 * Tenant Settings Page
 * Tenant-aware configuration settings
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  Alert,
  Divider,
  Switch,
  FormControlLabel,
  Chip,
  CircularProgress,
  Autocomplete,
} from '@mui/material';
import { Business as TenantIcon } from '@mui/icons-material';
import { apiGet, apiPut } from '../../utils/apiClient';
import logger from '../../utils/logger';
import { useTenant } from '../../contexts/TenantContext';

function TenantSettingsPage() {
  const { tenant, reload: reloadTenant } = useTenant();
  const [settings, setSettings] = useState({
    systemName: '',
    companyName: '',
    companyAddress: '',
    companyPhone: '',
    companyEmail: '',
    defaultCurrency: 'ZAR',
    dateFormat: 'YYYY-MM-DD',
    timezone: 'Africa/Johannesburg',
    enableEmailNotifications: true,
    enableAuditLogging: true,
    max_backdate_days_contract: '30',
    max_backdate_days_historical: '3650',
    max_backdate_days_rate_change: '30',
    maturity_grace_period_days: '30',
    contract_require_activation: false,
    contract_require_likelihood_to_roll: false,
    auto_create_capital_receipt_on_approve: false,
    rounding_precision: '3',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);

      if (tenant?.id) {
        // Load tenant-specific settings
        const response = await apiGet(`/tenants/${tenant.id}/settings`);
        if (response.success && response.data) {
          // Convert array of settings to object
          const settingsObj = {};
          response.data.forEach(s => {
            settingsObj[s.setting_key] = s.setting_value;
          });
          setSettings(prev => ({ ...prev, ...settingsObj, systemName: tenant.systemName || '' }));
        }
      } else {
        // Load global settings (fallback)
        const response = await apiGet('/admin/settings');
        if (response.data) {
          setSettings(prev => ({ ...prev, ...response.data }));
        }
      }
    } catch (error) {
      logger.error('Error loading settings', { message: error.message, status: error.response?.status });
    } finally {
      setLoading(false);
    }
  }, [tenant]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage(null);

      if (tenant?.id) {
        // Save tenant-specific settings
        await apiPut(`/tenants/${tenant.id}/settings`, {
          systemName: settings.systemName,
          companyName: settings.companyName,
          companyAddress: settings.companyAddress,
          companyPhone: settings.companyPhone,
          companyEmail: settings.companyEmail,
          defaultCurrency: settings.defaultCurrency,
          dateFormat: settings.dateFormat,
          timezone: settings.timezone,
          enableEmailNotifications: settings.enableEmailNotifications,
          enableAuditLogging: settings.enableAuditLogging,
          max_backdate_days_contract: settings.max_backdate_days_contract,
          max_backdate_days_historical: settings.max_backdate_days_historical,
          max_backdate_days_rate_change: settings.max_backdate_days_rate_change,
          maturity_grace_period_days: settings.maturity_grace_period_days,
          contract_require_activation: settings.contract_require_activation,
          contract_require_likelihood_to_roll: settings.contract_require_likelihood_to_roll,
          auto_create_capital_receipt_on_approve: settings.auto_create_capital_receipt_on_approve,
          rounding_precision: settings.rounding_precision,
        });
        setMessage({ type: 'success', text: `Settings saved for tenant: ${tenant.name}` });
        // Refresh tenant context to update the system name in the navbar
        reloadTenant();
      } else {
        // Save global settings
        await apiPut('/admin/settings', settings);
        setMessage({ type: 'success', text: 'Global settings saved successfully' });
      }
    } catch (error) {
      logger.error('Error saving settings', { message: error.message, status: error.response?.status });
      setMessage({ type: 'error', text: 'Failed to save settings' });
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">Tenant Settings</Typography>
        {tenant && (
          <Chip
            icon={<TenantIcon />}
            label={`Tenant: ${tenant.name}`}
            color="primary"
            variant="outlined"
          />
        )}
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {tenant
          ? `Configure settings for ${tenant.name}. These settings apply to all users in this tenant.`
          : 'Configure global system settings. These apply when no tenant context is available.'}
      </Typography>
      {message && (
        <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Branding
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={12}>
            <TextField
              fullWidth
              label="System Name"
              value={settings.systemName}
              onChange={handleChange('systemName')}
              helperText="This name appears in the header bar for all users"
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" gutterBottom>
          Company Information
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <TextField
              fullWidth
              label="Company Name"
              value={settings.companyName}
              onChange={handleChange('companyName')}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <TextField
              fullWidth
              label="Company Email"
              value={settings.companyEmail}
              onChange={handleChange('companyEmail')}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <TextField
              fullWidth
              label="Company Phone"
              value={settings.companyPhone}
              onChange={handleChange('companyPhone')}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <TextField
              fullWidth
              label="Company Address"
              value={settings.companyAddress}
              onChange={handleChange('companyAddress')}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" gutterBottom>
          Display Settings
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <TextField
              fullWidth
              label="Default Currency"
              value={settings.defaultCurrency}
              onChange={handleChange('defaultCurrency')}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <TextField
              fullWidth
              label="Date Format"
              value={settings.dateFormat}
              onChange={handleChange('dateFormat')}
              helperText="e.g., YYYY-MM-DD, DD/MM/YYYY"
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <Autocomplete
              freeSolo
              options={[
                'Africa/Johannesburg',
                'Africa/Nairobi',
                'Africa/Lagos',
                'Africa/Cairo',
                'Africa/Windhoek',
                'Africa/Harare',
                'Europe/London',
                'Europe/Paris',
                'America/New_York',
                'America/Chicago',
                'America/Los_Angeles',
                'Asia/Dubai',
                'Asia/Singapore',
                'Asia/Tokyo',
                'Australia/Sydney',
                'Pacific/Auckland',
                'UTC',
              ]}
              value={settings.timezone || 'Africa/Johannesburg'}
              onChange={(e, newValue) => setSettings(prev => ({ ...prev, timezone: newValue || 'Africa/Johannesburg' }))}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  label="Timezone"
                  helperText="IANA timezone for business day calculations (e.g., Africa/Johannesburg)"
                />
              )}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" gutterBottom>
          Features
        </Typography>
        <Box sx={{ mb: 3 }}>
          <FormControlLabel
            control={
              <Switch
                checked={settings.enableEmailNotifications}
                onChange={handleChange('enableEmailNotifications')}
              />
            }
            label="Enable Email Notifications"
          />
          <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4, mb: 1 }}>
            Send email notifications for contract events, payment reminders, etc.
          </Typography>

          <FormControlLabel
            control={
              <Switch
                checked={settings.enableAuditLogging}
                onChange={handleChange('enableAuditLogging')}
              />
            }
            label="Enable Audit Logging"
          />
          <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4 }}>
            Log all changes to contracts, payments, and settings for compliance.
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" gutterBottom>
          Business Rules
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <TextField
              fullWidth
              label="Max Backdate Days (Contracts)"
              type="number"
              value={settings.max_backdate_days_contract}
              onChange={handleChange('max_backdate_days_contract')}
              helperText="Maximum number of days in the past for contract deposit dates"
              inputProps={{ min: 0, max: 365 }}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <TextField
              fullWidth
              label="Max Backdate Days (Historical Contracts)"
              type="number"
              value={settings.max_backdate_days_historical}
              onChange={handleChange('max_backdate_days_historical')}
              helperText="Maximum days in the past for pre-go-live historical contract start dates"
              inputProps={{ min: 0, max: 7300 }}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <TextField
              fullWidth
              label="Max Backdate Days (Rate Changes)"
              type="number"
              value={settings.max_backdate_days_rate_change}
              onChange={handleChange('max_backdate_days_rate_change')}
              helperText="Maximum number of days in the past for rate change effective dates"
              inputProps={{ min: 0, max: 365 }}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <TextField
              fullWidth
              label="Maturity Grace Period (days)"
              type="number"
              value={settings.maturity_grace_period_days}
              onChange={handleChange('maturity_grace_period_days')}
              helperText="Days after maturity during which an 'Action Required' banner is shown on the contract"
              inputProps={{ min: 0, max: 365 }}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <TextField
              fullWidth
              label="Rounding Precision (decimal places)"
              type="number"
              value={settings.rounding_precision}
              onChange={handleChange('rounding_precision')}
              helperText="Number of decimal places for financial calculations (interest, capital, settlements)"
              inputProps={{ min: 0, max: 6 }}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" gutterBottom>
          Contract Workflow
        </Typography>
        <Box sx={{ mb: 3 }}>
          <FormControlLabel
            control={
              <Switch
                checked={settings.contract_require_activation === true || settings.contract_require_activation === 'true'}
                onChange={handleChange('contract_require_activation')}
              />
            }
            label="Require Separate Activation Step"
          />
          <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4 }}>
            When enabled, approved contracts must be separately activated before they become active and the amortization schedule is generated. When disabled, contracts go directly to Active status upon approval.
          </Typography>
        </Box>
        <Box sx={{ mb: 3 }}>
          <FormControlLabel
            control={
              <Switch
                checked={settings.contract_require_likelihood_to_roll === true || settings.contract_require_likelihood_to_roll === 'true'}
                onChange={handleChange('contract_require_likelihood_to_roll')}
              />
            }
            label="Require Likelihood to Roll"
          />
          <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4 }}>
            When enabled, users must select a Likelihood to Roll value for every contract (and for each investor on multi-investor contracts) before the contract wizard can proceed.
          </Typography>
        </Box>
        <Box sx={{ mb: 3 }}>
          <FormControlLabel
            control={
              <Switch
                checked={settings.auto_create_capital_receipt_on_approve === true || settings.auto_create_capital_receipt_on_approve === 'true'}
                onChange={handleChange('auto_create_capital_receipt_on_approve')}
              />
            }
            label="Auto-Create Capital Receipt on Approval"
          />
          <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4 }}>
            When enabled, approving a contract that has a Funds Received Date automatically creates the matching capital receipt against the Capital Drawdown. When disabled (default), the incoming capital flow must be matched manually on the Payment Matching screen.
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button onClick={loadSettings} disabled={saving}>
            Reset
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}

export default TenantSettingsPage;
